import { MapPin, Check, Star, Trash2, Plus } from 'lucide-react';

// Gym Card for Favorites Tab (large, simple, quick access)
export const FavoriteGymCard = ({ gym, isSelected, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full p-6 rounded-xl border-2 transition-all touch-manipulation ${
        isSelected
          ? 'border-teal-500 bg-teal-50 shadow-retro'
          : 'border-slate-900 hover:border-teal-500 hover:bg-teal-50 shadow-retro hover:shadow-retro-hover'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 text-left min-w-0">
          <h3 className="text-xl font-bold text-slate-900 truncate mb-1">
            {gym.name}
          </h3>
          {(gym.city || gym.state) && (
            <p className="text-sm text-slate-600 flex items-center gap-1">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {gym.city}{gym.city && gym.state ? ', ' : ''}{gym.state}
              </span>
            </p>
          )}
        </div>

        {isSelected && (
          <div className="flex-shrink-0 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center border-2 border-slate-900 ml-4">
            <Check className="w-5 h-5 text-white" />
          </div>
        )}
      </div>
    </button>
  );
};

// Gym Card for My Gyms Tab (with management actions)
export const ManageableGymCard = ({
  gym,
  isSelected,
  onSelect,
  onSetPrimary,
  onRemove,
  isSettingPrimary
}) => {
  return (
    <div className="w-full p-5 rounded-xl border-2 border-slate-900 bg-white shadow-retro">
      <div className="flex items-start justify-between mb-3">
        <button
          onClick={onSelect}
          className="flex-1 text-left hover:text-teal-600 transition-colors min-w-0"
        >
          <h3 className="text-lg font-bold text-slate-900 truncate mb-1">
            {gym.name}
          </h3>
          {(gym.city || gym.state) && (
            <p className="text-sm text-slate-600 flex items-center gap-1">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {gym.city}{gym.city && gym.state ? ', ' : ''}{gym.state}
              </span>
            </p>
          )}
        </button>

        {isSelected && (
          <div className="flex-shrink-0 w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center border-2 border-slate-900 ml-3">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {!gym.isPrimary && (
          <button
            onClick={onSetPrimary}
            disabled={isSettingPrimary}
            className="px-3 py-1.5 bg-white hover:bg-sunshine-50 disabled:bg-slate-100 text-sunshine-700 disabled:text-slate-400 font-bold text-sm rounded-lg border-2 border-sunshine-500 disabled:border-slate-300 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
          >
            <Star className="w-4 h-4 inline mr-1" />
            {isSettingPrimary ? 'Setting...' : 'Set as Primary'}
          </button>
        )}
        {gym.isPrimary && (
          <span className="px-3 py-1.5 bg-sunshine-100 text-sunshine-800 border-2 border-sunshine-500 text-sm font-bold rounded-full inline-flex items-center">
            <Star className="w-4 h-4 mr-1 fill-sunshine-600 text-sunshine-600" />
            Primary Gym
          </span>
        )}

        <button
          onClick={onRemove}
          className="ml-auto p-2 hover:bg-coral-50 rounded-lg border-2 border-transparent hover:border-coral-300 transition-all touch-manipulation"
          aria-label="Remove gym"
        >
          <Trash2 className="w-4 h-4 text-coral-500" />
        </button>
      </div>
    </div>
  );
};

// Gym Card for Find Gyms Tab (with Book Once and Add options)
export const SearchableGymCard = ({
  gym,
  isAlreadyAdded,
  onBookOnce,
  onAdd,
  isAdding
}) => {
  return (
    <div className="w-full p-5 rounded-xl border-2 border-slate-900 bg-white shadow-retro">
      <div className="mb-4">
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-lg font-bold text-slate-900 truncate flex-1">
            {gym.name}
          </h3>
          {isAlreadyAdded && (
            <span className="flex-shrink-0 ml-2 px-2 py-1 bg-teal-100 text-teal-700 border-2 border-teal-500 text-xs font-bold rounded-full">
              Added ✓
            </span>
          )}
        </div>
        {(gym.city || gym.state) && (
          <p className="text-sm text-slate-600 flex items-center gap-1">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="truncate">
              {gym.city}{gym.city && gym.state ? ', ' : ''}{gym.state}
            </span>
          </p>
        )}
        {gym.address_line_1 && (
          <p className="text-xs text-slate-500 truncate mt-1">
            {gym.address_line_1}{gym.address_line_2 ? `, ${gym.address_line_2}` : ''}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={onBookOnce}
          className="flex-1 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
        >
          Book Once
        </button>

        {!isAlreadyAdded && (
          <button
            onClick={onAdd}
            disabled={isAdding}
            className="px-4 py-2.5 bg-white hover:bg-teal-50 disabled:bg-slate-100 text-teal-600 disabled:text-slate-400 font-bold rounded-lg border-2 border-teal-500 disabled:border-slate-300 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
            aria-label="Add to my gyms"
          >
            {isAdding ? (
              <span className="text-sm">Adding...</span>
            ) : (
              <Plus className="w-5 h-5" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

// Simple gym card for basic selection (used in keyboard navigation highlighting)
export const SimpleGymCard = ({ gym, isSelected, isHighlighted, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-lg border-2 transition-all touch-manipulation ${
        isSelected
          ? 'border-teal-500 bg-teal-50 shadow-retro'
          : isHighlighted
          ? 'border-purple-500 bg-purple-50 shadow-retro'
          : 'border-slate-900 hover:border-teal-500 hover:bg-teal-50 shadow-retro hover:shadow-retro-hover'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="font-semibold text-gray-900 truncate">
              {gym.name}
            </h3>
            {gym.isPrimary && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-sunshine-100 text-sunshine-800 border-2 border-sunshine-500 text-xs font-bold rounded-full whitespace-nowrap">
                <Star className="w-3 h-3 fill-sunshine-600 text-sunshine-600" />
                Primary
              </span>
            )}
          </div>
          {(gym.city || gym.state) && (
            <div className="flex items-center gap-1.5 text-sm text-gray-600">
              <MapPin className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">
                {gym.city}{gym.city && gym.state ? ', ' : ''}{gym.state}
              </span>
            </div>
          )}
        </div>

        {isSelected && (
          <div className="flex-shrink-0 w-6 h-6 bg-teal-500 border-2 border-slate-900 rounded-full flex items-center justify-center">
            <Check className="w-4 h-4 text-white" />
          </div>
        )}
      </div>
    </button>
  );
};
